// fallow-ignore-file complexity
import { Flex, FormattedRelativeTime, Spinner, Stack, Text, TextLabel } from '@customerio/pluma-components/react';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import type { CustomFormResult } from '@/services/auth/types';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { formatNumber } from '@/utils/number';
import { capitalize } from '@/utils/string';
import { CUSTOM_FORM_TYPE_LABELS } from '../constants';

type InfoRowProps = { label: string; children: React.ReactNode };

function InfoRow({ label, children }: InfoRowProps) {
  return (
    <Flex direction="column" gap="025">
      <TextLabel>{label}</TextLabel>
      <Text size="sm">{children}</Text>
    </Flex>
  );
}

type FormPreviewProps = { result: CustomFormResult };

export function FormPreview({ result }: FormPreviewProps) {
  const taxonomy = useProductTaxonomy();
  const { env_id, form_type, form_id, status, created_at, last_activity_at, html_form_url } = result.other;
  const formTypeLabel = CUSTOM_FORM_TYPE_LABELS[form_type] ?? form_type;
  const statusLabel = capitalize(status);

  const { data: segmentsData, isLoading: isSegmentsLoading } = useHydraUIAPIQuery({
    queryKey: ['getSegments', env_id, form_id],
    queryFn: ({ signal }) =>
      hydraUiApi.segments.list({
        params: { path: { environment_id: String(env_id) }, query: { linked_form_id: form_id } },
        signal,
      }),
    queryConfig: { enabled: env_id > 0, staleTime: 5 * 60 * 1000 },
  });

  const linkedSegment = segmentsData?.segments[0];
  const segmentId = linkedSegment?.id;
  const isLinkedSegmentArchived = linkedSegment != null && 'archived' in linkedSegment && linkedSegment.archived;

  const { data: countData, isLoading: isCountLoading } = useHydraUIAPIQuery({
    queryKey: ['getSegmentCount', env_id, segmentId],
    queryFn: ({ signal }) =>
      hydraUiApi.segments.getCount({
        params: { path: { environment_id: String(env_id), segment_id: String(segmentId) } },
        signal,
      }),
    queryConfig: { enabled: env_id > 0 && segmentId != null, staleTime: 5 * 60 * 1000 },
  });

  const isPeopleLoading = isSegmentsLoading || (segmentId != null && isCountLoading);

  return (
    <Stack gap="200">
      <InfoRow label="Form ID">{form_id}</InfoRow>
      <InfoRow label="Form type">{formTypeLabel}</InfoRow>
      <InfoRow label="Status">{statusLabel}</InfoRow>
      <InfoRow label={`${taxonomy.term('people.titlePlural')} ${taxonomy.t('people.relativePronoun')} submitted`}>
        {isPeopleLoading ? (
          <Spinner />
        ) : linkedSegment == null || isLinkedSegmentArchived ? (
          '—'
        ) : countData?.count != null ? (
          formatNumber(countData.count)
        ) : (
          '—'
        )}
      </InfoRow>
      {html_form_url != null ? <InfoRow label="Scanned URL">{html_form_url}</InfoRow> : null}
      {last_activity_at != null ? (
        <InfoRow label="Last activity">
          <FormattedRelativeTime date={last_activity_at} withTooltip />
        </InfoRow>
      ) : null}
      {created_at != null ? (
        <InfoRow label="Created">
          <FormattedRelativeTime date={created_at} withTooltip />
        </InfoRow>
      ) : null}
    </Stack>
  );
}
